import * as THREE from 'three';
import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  compute3DMeshDataAndPosition,
  loadTexture,
  replaceGeometryAttributesOnGeometry,
} from '../../utils';
import * as constantsViewer from '../../constants';
import { createReactThreeGeometry } from '../../reactThreeUtils';
import { useSpring, a } from 'react-spring/three';
import textureCrossPiece from 'assets/images/textures/whiteash.jpg';
import data from './data/crossPiece';
import { onPointerDown, onPointerUp } from '../../helpers';
import { ConfiguratorMode } from 'containers/ModeBar/reducer';

export function CrossPiece({
  crossPiece,
  configuratorMode,
  rack = null,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  // prevCabinetWidth used for animate reinforcement,
  // piece added at its last width and after animated
  // to cabinet current width
  const [prevCabinetWidth, setCabinetWidth] = useState(cabinetWidth);

  const type = crossPiece.base.type;
  const cpPosition = crossPiece.position;
  let yOffset = 0;
  const modelRackHeight = 410;
  if (rack && (type === 'ED' || type === 'EP')) yOffset -= modelRackHeight;

  let crossPieceReinforcement = null;
  if (crossPiece.base.reinforce) {
    let yOffsetReinforce = 0;
    if (!rack) yOffsetReinforce += modelRackHeight;
    crossPieceReinforcement = (
      <CrossPieceReinforcement
        yOffset={yOffsetReinforce}
        cpPosition={cpPosition}
        prevCabinetWidth={prevCabinetWidth}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        unselected={unselected}
      />
    );
  }

  useEffect(() => {
    setCabinetWidth(cabinetWidth);
  }, [cabinetWidth]);

  return (
    <group
      onPointerDown={e => onPointerDown(e)}
      onPointerUp={e =>
        onPointerUp(e, e => {
          if (configuratorMode !== ConfiguratorMode.selector)
            e.stopPropagation();
        })
      }
    >
      <CrossPieceWithoutReinforcement
        type={type}
        yOffset={yOffset}
        cpPosition={cpPosition}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        unselected={unselected}
      />
      {crossPieceReinforcement}
    </group>
  );
}

function CrossPieceWithoutReinforcement({
  type,
  yOffset,
  cpPosition = 'B',
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  const mesh = useRef();

  const baseDataAllTypes = useMemo(() => compute3DMeshDataAndPositionCP(), [
    data.crossPiecePP,
    data.crossPieceEP,
    data.crossPieceED,
    data.crossPieceDD,
  ]);

  const baseDataOrigin = useMemo(() => getBaseData(type, baseDataAllTypes), [
    baseDataAllTypes,
  ]);

  const [prevType, setPrevType] = useState(type);
  const [prevYOffset, setYOffset] = useState(yOffset);

  const baseData = getBaseData(prevType, baseDataAllTypes);

  const baseDataCurrent = getBaseData(type, baseDataAllTypes);

  const texture = useMemo(() => loadTexture(textureCrossPiece), [
    textureCrossPiece,
  ]);

  const [spring, set] = useSpring(() => ({
    position: getPosition(
      type,
      baseData.position,
      baseData.size,
      cabinetWidth,
      cabinetDepth,
      yOffset,
      cpPosition,
    ),
    scale: getScale(
      type,
      baseData.size,
      baseData.size,
      cabinetWidth,
      cabinetDepth,
      cpPosition,
    ),
    rotation: getRotation(type, cpPosition),
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = mesh.current;
    if (self) {
      self.geometry.computeBoundingBox();
      let size = new THREE.Vector3();
      self.geometry.boundingBox.getSize(size);

      set({
        position: getPosition(
          prevType,
          baseData.position,
          baseData.size,
          cabinetWidth,
          cabinetDepth,
          prevYOffset,
          cpPosition,
        ),
        scale: getScale(
          prevType,
          baseData.size,
          size,
          cabinetWidth,
          cabinetDepth,
          cpPosition,
        ),
        immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
        onRest: () => {
          if (type != prevType) {
            changeGeometry(self, type, baseDataAllTypes);
            set({
              position: getPosition(
                type,
                baseDataCurrent.position,
                baseDataCurrent.size,
                cabinetWidth,
                cabinetDepth,
                yOffset,
                cpPosition,
              ),
              scale: getScale(
                type,
                baseDataCurrent.size,
                size,
                cabinetWidth,
                cabinetDepth,
                cpPosition,
              ),
              rotation: getRotation(type, cpPosition),
              onRest: null,
              immediate: true,
            });
          }
        },
      });

      setPrevType(type);
      setYOffset(yOffset);
    }
  }, [cabinetWidth, cabinetDepth, yOffset, type]);

  return (
    <a.mesh
      ref={mesh}
      receiveShadow
      castShadow
      position={spring.position}
      scale={spring.scale}
      rotation={spring.rotation}
    >
      {createReactThreeGeometry(baseDataOrigin)}
      <meshPhongMaterial
        attach="material"
        color={constantsViewer.MATERIAL_CROSSPIECE_COLOR}
        specular={constantsViewer.MATERIAL_CROSSPIECE_SPECULAR}
        shininess={constantsViewer.MATERIAL_CROSSPIECE_SHININESS}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
        map={texture}
        map-minFilter={THREE.LinearMipMapLinearFilter}
        map-magFilter={THREE.LinearFilter}
      />
    </a.mesh>
  );
}

function CrossPieceReinforcement({
  yOffset,
  cpPosition = 'B',
  prevCabinetWidth = 1000,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  const mesh = useRef();

  const baseData = useMemo(
    () => compute3DMeshDataAndPosition(data.crossPieceReinforcement),
    [data.crossPieceReinforcement],
  );

  const [spring, set] = useSpring(() => ({
    position: getPositionReinforcement(
      baseData.position,
      baseData.size,
      prevCabinetWidth,
      cabinetDepth,
      yOffset,
      cpPosition,
    ),
    scale: getScaleReinforcement(
      baseData.size,
      baseData.size,
      prevCabinetWidth,
      cabinetDepth,
      cpPosition,
    ),
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = mesh.current;
    if (self) {
      self.geometry.computeBoundingBox();
      let size = new THREE.Vector3();
      self.geometry.boundingBox.getSize(size);

      set({
        position: getPositionReinforcement(
          baseData.position,
          baseData.size,
          cabinetWidth,
          cabinetDepth,
          yOffset,
          cpPosition,
        ),
        scale: getScaleReinforcement(
          baseData.size,
          size,
          cabinetWidth,
          cabinetDepth,
          cpPosition,
        ),
      });
    }
  }, [cabinetWidth, cabinetDepth, yOffset]);

  return (
    <a.mesh
      ref={mesh}
      receiveShadow
      castShadow
      position={spring.position}
      scale={spring.scale}
      rotation={getRotationReinforcement(cpPosition)}
    >
      {createReactThreeGeometry(baseData)}
      <meshPhongMaterial
        attach="material"
        color={0xffffff}
        specular={0x919191}
        shininess={4.92458}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </a.mesh>
  );
}

function changeGeometry(self, type, baseDataAllTypes) {
  let newData = getBaseData(type, baseDataAllTypes);

  replaceGeometryAttributesOnGeometry(self.geometry, newData);
}

function compute3DMeshDataAndPositionCP() {
  return {
    crossPieceED: compute3DMeshDataAndPosition(data.crossPieceED),
    crossPieceEP: compute3DMeshDataAndPosition(data.crossPieceEP),
    crossPiecePP: compute3DMeshDataAndPosition(data.crossPiecePP),
    crossPieceDD: compute3DMeshDataAndPosition(data.crossPieceDD),
  };
}

function getBaseData(type, baseDataAllTypes) {
  switch (type) {
    case 'ED':
      return baseDataAllTypes.crossPieceED;

    case 'EP':
      return baseDataAllTypes.crossPieceEP;

    case 'PP':
      return baseDataAllTypes.crossPiecePP;

    case 'DD':
      return baseDataAllTypes.crossPieceDD;

    default:
      return null;
  }
}

function getRotation(type, cpPosition) {
  switch (type) {
    case 'ED':
      return getRotationED(cpPosition);

    case 'EP':
      return getRotationEP(cpPosition);

    case 'PP':
      return getRotationPP(cpPosition);

    case 'DD':
      return getRotationDD(cpPosition);

    default:
      return null;
  }
}

function getRotationPP(cpPosition) {
  switch (cpPosition) {
    case 'B':
      return [0, Math.PI, 0];

    case 'R':
      return [0, Math.PI / 2, 0];

    case 'L':
      return [0, -Math.PI / 2, 0];

    case 'F':
      return [0, 0, 0];

    default:
      return null;
  }
}

function getRotationDD(cpPosition) {
  switch (cpPosition) {
    case 'F':
      return [0, 0, 0];

    case 'B':
      return [0, -Math.PI, 0];

    default:
      console.error(
        'CrossPiece DD for position ' + cpPosition + ' not implemented',
      );
      return null;
  }
}

function getRotationED(cpPosition) {
  switch (cpPosition) {
    case 'F':
      return [0, 0, 0];

    case 'B':
      return [0, -Math.PI, 0];

    default:
      console.error(
        'CrossPiece ED for position ' + cpPosition + ' not implemented',
      );
      return null;
  }
}

function getRotationEP(cpPosition) {
  switch (cpPosition) {
    case 'B':
      return [0, 0, 0];

    case 'F':
      return [0, -Math.PI, 0];

    case 'L':
      return [0, Math.PI / 2, 0];

    case 'R':
      return [0, -Math.PI / 2, 0];

    default:
      console.error(
        'CrossPiece EP for position ' + cpPosition + ' not implemented',
      );
      return null;
  }
}

function getRotationReinforcement(cpPosition) {
  switch (cpPosition) {
    case 'B':
      return [0, 0, 0];

    case 'F':
      return [0, -Math.PI, 0];

    case 'R':
      return [0, -Math.PI / 2, 0];

    case 'L':
      return [0, Math.PI / 2, 0];

    default:
      return null;
  }
}

function getScale(
  type,
  baseSize,
  size,
  cabinetWidth,
  cabinetDepth,
  cpPosition,
) {
  switch (type) {
    case 'ED':
      return getScaleED(baseSize, size, cabinetWidth, cpPosition);

    case 'EP':
      return getScaleEP(baseSize, size, cabinetWidth, cabinetDepth, cpPosition);

    case 'PP':
      return getScalePP(baseSize, size, cabinetWidth, cabinetDepth, cpPosition);

    case 'DD':
      return getScaleDD(baseSize, size, cabinetWidth, cpPosition);

    default:
      return null;
  }
}

function getScalePP(baseSize, size, cabinetWidth, cabinetDepth, cpPosition) {
  let spaceWidthNoScale = constantsViewer.MODEL_CABINET_WIDTH - baseSize.x;
  switch (cpPosition) {
    case 'B':
    case 'F':
      return [(cabinetWidth - spaceWidthNoScale) / size.x, 1, 1];

    case 'R':
    case 'L':
      return [(cabinetDepth - spaceWidthNoScale - 1) / size.x, 1, 1];

    default:
      return null;
  }
}

function getScaleEP(baseSize, size, cabinetWidth, cabinetDepth, cpPosition) {
  let spaceWidthNoScale = constantsViewer.MODEL_CABINET_WIDTH - baseSize.x;
  switch (cpPosition) {
    case 'B':
    case 'F':
      return [(cabinetWidth - spaceWidthNoScale) / size.x, 1, 1];

    case 'R':
    case 'L':
      return [(cabinetDepth - spaceWidthNoScale - 1) / size.x, 1, 1];

    default:
      return null;
  }
}

function getScaleDD(baseSize, size, cabinetWidth, cpPosition) {
  let spaceWidthNoScale = constantsViewer.MODEL_CABINET_WIDTH - baseSize.x;
  switch (cpPosition) {
    case 'F':
      return [(cabinetWidth - spaceWidthNoScale) / size.x, 1, 1];

    case 'B':
      return [(cabinetWidth - spaceWidthNoScale) / size.x, 1, 1];

    default:
      console.error(
        'CrossPiece DD for position ' + cpPosition + ' not implemented',
      );
      return null;
  }
}

function getScaleED(baseSize, size, cabinetWidth, cpPosition) {
  let spaceWidthNoScale = constantsViewer.MODEL_CABINET_WIDTH - baseSize.x;
  switch (cpPosition) {
    case 'F':
      return [(cabinetWidth - spaceWidthNoScale) / size.x, 1, 1];

    case 'B':
      return [(cabinetWidth - spaceWidthNoScale) / size.x, 1, 1];

    default:
      console.error(
        'CrossPiece ED for position ' + cpPosition + ' not implemented',
      );
      return null;
  }
}

function getScaleReinforcement(
  baseSize,
  size,
  cabinetWidth,
  cabinetDepth,
  cpPosition,
) {
  let spaceWidthNoScale = constantsViewer.MODEL_CABINET_WIDTH - baseSize.x;
  switch (cpPosition) {
    case 'B':
      return [(cabinetWidth - spaceWidthNoScale) / size.x, 1, 1];

    case 'F':
      return [(cabinetWidth - spaceWidthNoScale) / size.x, 1, 1];

    case 'R':
      return [(cabinetDepth - spaceWidthNoScale) / size.x, 1, 1];

    case 'L':
      return [(cabinetDepth - spaceWidthNoScale) / size.x, 1, 1];

    default:
      return null;
  }
}

function getPosition(
  type,
  basePosition,
  size,
  cabinetWidth,
  cabinetDepth,
  yOffset,
  cpPosition,
) {
  switch (type) {
    case 'ED':
      return getPositionED(
        basePosition,
        size,
        cabinetWidth,
        cabinetDepth,
        yOffset,
        cpPosition,
      );

    case 'EP':
      return getPositionEP(
        basePosition,
        size,
        cabinetWidth,
        cabinetDepth,
        yOffset,
        cpPosition,
      );

    case 'PP':
      return getPositionPP(
        basePosition,
        size,
        cabinetWidth,
        cabinetDepth,
        yOffset,
        cpPosition,
      );

    case 'DD':
      return getPositionDD(
        basePosition,
        size,
        cabinetWidth,
        cabinetDepth,
        yOffset,
        cpPosition,
      );

    default:
      return null;
  }
}

function getPositionPP(
  basePosition,
  size,
  cabinetWidth,
  cabinetDepth,
  yOffset,
  cpPosition,
) {
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;
  const crossPieceDepth = cabinetDepth - 9;

  switch (cpPosition) {
    case 'B':
      return [
        basePosition.x,
        basePosition.y + yOffset,
        basePosition.z - cabinetDiffDepth / 2,
      ];

    case 'R':
      return [
        basePosition.x + cabinetWidth / 2 - size.z / 2 - 5,
        basePosition.y + yOffset,
        basePosition.z + crossPieceDepth / 2 - cabinetDiffDepth / 2 - 5,
      ];

    case 'L':
      return [
        basePosition.x - cabinetWidth / 2 + size.z / 2 + 3,
        basePosition.y + yOffset,
        basePosition.z + crossPieceDepth / 2 - cabinetDiffDepth / 2 - 5,
      ];

    case 'F':
      return [
        basePosition.x,
        basePosition.y + yOffset,
        basePosition.z - cabinetDiffDepth / 2 + cabinetDepth - 25,
      ];

    default:
      return null;
  }
}

function getPositionEP(
  basePosition,
  size,
  cabinetWidth,
  cabinetDepth,
  yOffset,
  cpPosition,
) {
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;
  const crossPieceDepth = cabinetDepth - 9;

  switch (cpPosition) {
    case 'B':
      return [
        basePosition.x,
        basePosition.y + yOffset,
        basePosition.z - cabinetDiffDepth / 2,
      ];

    case 'F':
      return [
        basePosition.x,
        basePosition.y + yOffset,
        basePosition.z - cabinetDiffDepth / 2 + cabinetDepth - 25,
      ];

    case 'R':
      return [
        basePosition.x + cabinetWidth / 2 - size.z / 2 - 5,
        basePosition.y + yOffset,
        basePosition.z + crossPieceDepth / 2 - cabinetDiffDepth / 2 - 5,
      ];

    case 'L':
      return [
        basePosition.x - cabinetWidth / 2 + size.z / 2 + 2,
        basePosition.y + yOffset,
        basePosition.z + crossPieceDepth / 2 - cabinetDiffDepth / 2 - 5,
      ];

    default:
      console.error(
        'CrossPiece EP for position ' + cpPosition + ' not implemented',
      );
      return null;
  }
}

function getPositionDD(
  basePosition,
  size,
  cabinetWidth,
  cabinetDepth,
  yOffset,
  cpPosition,
) {
  const cabinetDiffWith = cabinetWidth - constantsViewer.MODEL_CABINET_WIDTH;
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;

  switch (cpPosition) {
    case 'F':
      return [
        basePosition.x,
        basePosition.y + yOffset,
        basePosition.z + cabinetDiffDepth / 2,
      ];

    case 'B':
      return [
        basePosition.x,
        basePosition.y + yOffset,
        basePosition.z + cabinetDiffDepth / 2 - cabinetDepth + 34,
      ];

    default:
      console.error(
        'CrossPiece DD for position ' + cpPosition + ' not implemented',
      );
      return null;
  }
}

function getPositionED(
  basePosition,
  size,
  cabinetWidth,
  cabinetDepth,
  yOffset,
  cpPosition,
) {
  const cabinetDiffWith = cabinetWidth - constantsViewer.MODEL_CABINET_WIDTH;
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;

  let crossPieceDepth = cabinetDepth - 20;

  switch (cpPosition) {
    case 'F':
      return [
        basePosition.x,
        basePosition.y + yOffset,
        basePosition.z + cabinetDiffDepth / 2,
      ];

    case 'B':
      return [
        basePosition.x,
        basePosition.y + yOffset,
        basePosition.z + cabinetDiffDepth / 2 - cabinetDepth + 34,
      ];

    default:
      console.error(
        'CrossPiece ED for position ' + cpPosition + ' not implemented',
      );
      return null;
  }
}

function getPositionReinforcement(
  basePosition,
  size,
  cabinetWidth,
  cabinetDepth,
  yOffset,
  cpPosition,
) {
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;
  const crossPieceDepth = cabinetDepth - 9;

  switch (cpPosition) {
    case 'B':
      return [
        basePosition.x,
        basePosition.y + yOffset + 2,
        basePosition.z - cabinetDiffDepth / 2 + 3,
      ];

    case 'F':
      return [
        basePosition.x,
        basePosition.y + yOffset + 2,
        basePosition.z - cabinetDiffDepth / 2 + cabinetDepth - 75,
      ];

    case 'R':
      return [
        basePosition.x + cabinetWidth / 2 - size.z / 2 - 26,
        basePosition.y + yOffset + 2,
        basePosition.z + crossPieceDepth / 2 - cabinetDiffDepth / 2 - 25,
      ];

    case 'L':
      return [
        basePosition.x - cabinetWidth / 2 + size.z / 2 + 24,
        basePosition.y + yOffset + 2,
        basePosition.z + crossPieceDepth / 2 - cabinetDiffDepth / 2 - 25,
      ];

    default:
      return null;
  }
}
